// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The shape of the Critical Systems & Plant band — which cards exist, and which
 * readouts each one holds.
 *
 * **Labels only. There are deliberately no values here.** This is the design's
 * structure, which is ours to state, not measurement, which is upstream's. It
 * exists so the band keeps its cards and its rhythm when nothing publishes the
 * numbers, instead of the whole section disappearing and taking the record of
 * what should be wired with it.
 *
 * `key` is the identifier a readout would arrive under, so this doubles as the
 * list of points the endpoint needs to serve. When it does, the panel renders
 * values against these same keys and this file stops being load-bearing.
 */
export interface PlantReadoutSpec {
  key: string;
  label: string;
  /** Unit the design shows for this readout, where it has a fixed one. */
  unit?: string;
}

export interface PlantGroupSpec {
  id: string;
  title: string;
  readouts: PlantReadoutSpec[];
}

export const PLANT_GROUPS: PlantGroupSpec[] = [
  {
    id: 'load-equipment',
    title: 'Load & Equipment',
    readouts: [
      { key: 'total_hvac_load', label: 'Total HVAC load', unit: 'kW' },
      { key: 'total_facility_load', label: 'Total facility load', unit: 'kW' },
      // Running/installed counts arrive as two points and are composed at render.
      { key: 'crah_running', label: 'CRAH running' },
      { key: 'chillers_running', label: 'Chillers running' },
    ],
  },
  {
    id: 'gallery-yard',
    title: 'Gallery & Yard',
    readouts: [
      { key: 'mep_gallery_temp', label: 'MEP gallery temp', unit: '°F' },
      { key: 'outdoor_air_temp', label: 'Outdoor air temp', unit: '°F' },
      { key: 'gen_day_tank_fuel', label: 'Gen day-tank fuel', unit: '%' },
      { key: 'ats_position', label: 'ATS position' },
    ],
  },
  {
    id: 'chilled-water',
    title: 'Chilled Water Plant',
    readouts: [
      { key: 'chws_header_temp', label: 'CHWS header temp', unit: '°F' },
      { key: 'chwr_header_temp', label: 'CHWR header temp', unit: '°F' },
      { key: 'chw_plant_delta_t', label: 'CHW plant ΔT', unit: '°F' },
      { key: 'chw_flow', label: 'CHW flow', unit: 'gpm' },
    ],
  },
];

/** The two bars of the facility-vs-HVAC comparison, in the design's order. */
export const LOAD_COMPARISON_TITLE = 'Facility vs HVAC load';
export const LOAD_COMPARISON_BARS = [
  { key: 'facility', label: 'Facility' },
  { key: 'hvac', label: 'HVAC', accent: true },
];
